import { useEffect } from 'react';
import { useTutorialContext } from '../contexts/TutorialContext';
import { TutorialId } from '../types/tutorial';

interface UseTutorialOptions {
  tutorialId: TutorialId;
  trigger: boolean;
  delay?: number; // Delay in ms before showing tutorial
}

export const useTutorial = ({
  tutorialId,
  trigger,
  delay = 500,
}: UseTutorialOptions) => {
  const { state, startTutorial, isTutorialShown } = useTutorialContext();

  useEffect(() => {
    // Don't show if user has skipped all tutorials
    if (state.skipAll) return;

    // Don't show if already shown
    if (isTutorialShown(tutorialId)) return;

    // Don't show if another tutorial is active
    if (state.currentTutorial && state.currentTutorial !== tutorialId) return;

    // Don't show if trigger condition is not met
    if (!trigger) return;

    // Start tutorial after delay
    const timeoutId = setTimeout(() => {
      startTutorial(tutorialId);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [
    tutorialId,
    trigger,
    delay,
    state.skipAll,
    state.currentTutorial,
    startTutorial,
    isTutorialShown,
  ]);

  return {
    isActive: state.currentTutorial === tutorialId,
    currentStep: state.currentStep,
  };
};
